import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import ModalConstants from 'app/shared/constants/ModalConstants';

import ModalActions from 'app/shared/actions/ModalActions';

import analyticsUtils from 'app/client/utils/analyticsUtils';

// Constellation Components
import {
    Button,
    Text,
} from '@zillow/constellation';

import {
    Row,
} from '@zillow/zrs-comet';

import SpinnerContainer from 'app/shared/components/SpinnerContainer';
import TourCardSelectable from 'app/shared/components/postingPath/TourCardSelectable';

// constants
import KeyCodeConstants from 'app/shared/constants/KeyCodeConstants';

// style
import './style.scss';

// Styled Components
import {
    StyledControlsDiv,
    StyledEvenCol,
    StyledLink,
} from './StyledComponents';

const propTypes = {
    defaultTours: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        createdDate: PropTypes.string.isRequired,
        creatorName: PropTypes.string.isRequired,
        imageSrc: PropTypes.string,
        src: PropTypes.string,
        state: PropTypes.oneOf(['active', 'inactive', 'empty']),
    })),
    shouldLoadTours: PropTypes.bool,
    isListingPublished: PropTypes.bool,
    onLoadTours: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(['floorplan', 'unit', 'amenity', 'singleFamily']),
    initialSelectedTourId: PropTypes.string,
};

const defaultProps = {
    defaultTours: [],
    shouldLoadTours: true,
    isListingPublished: false,
    mode: 'floorplan',
    initialSelectedTourId: null,
};

const HELPER_TEXT = {
    floorplan: 'Select a tour to showcase this floor plan.',
    unit: 'Select a tour to showcase this unit.',
    amenity: 'Only one amenity tour can be selected per property.',
    singleFamily: 'Only one 3d tour can be selected per property.',
};

const HEADER_TEXT = {
    floorplan: 'Floor plan',
    unit: 'Unit',
    amenity: 'Amenity',
    singleFamily: 'Property',
};

const SF_NOT_PUBLISHED_TEXT = 'We won’t able to add this 3d tour to your listing until the listing is live on our '
    + 'sites. Once this listing is live on our sites, please try this operation again.';

const ROW_SIZE = 2;

const onKeyUp = (event) => {
    if (event.keyCode === KeyCodeConstants.ENTER ||
        event.keyCode === KeyCodeConstants.SPACE) {
        event.target.click();
    }
};

const SelectTourModal = ({
    dispatch,
    shouldLoadTours,
    isListingPublished,
    onLoadTours,
    onSubmit,
    onCancel,
    mode,
    defaultTours,
    initialSelectedTourId,
}) => {
    const [isLoading, setIsLoading] = React.useState(shouldLoadTours);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [loadingErrorMessage, setLoadingErrorMessage] = React.useState();
    const [errorMessage, setErrorMessage] = React.useState();
    const [tours, setTours] = React.useState(defaultTours);
    const [selectedTourId, setSelectedTourId] = React.useState(initialSelectedTourId);

    React.useEffect(() => {
        if (shouldLoadTours) {
            onLoadTours()
                .then((loadedTours) => {
                    dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.LOAD_TOURS, {
                        label: AnalyticsConstants.TOUR_LOCATION.MODAL + AnalyticsConstants.TOUR_TYPE[mode],
                        value: loadedTours.length,
                    }));
                    setTours(loadedTours);
                    setIsLoading(false);
                    const selectedTour = loadedTours.filter(tour => tour.isSelected);
                    if (selectedTour.length > 0) {
                        setSelectedTourId(selectedTour[0].id);
                    }
                })
                .catch(() => {
                    setIsLoading(false);
                    setLoadingErrorMessage('Cannot retrieve 3d tours, please try again later.');
                });
        }
    }, [dispatch, mode, onLoadTours, shouldLoadTours]);

    const handleSubmit = () => {
        setIsSubmitting(true);
        if (mode === 'singleFamily' && !isListingPublished) {
            setErrorMessage(SF_NOT_PUBLISHED_TEXT);
            return;
        }
        return onSubmit(selectedTourId)
            .catch(() => {
                setIsSubmitting(false);
                setErrorMessage('Cannot associate this tour, please try again later.');
            });
    };

    const handleSelectTourId = (id) => {
        const isUnselecting = selectedTourId === id;
        const gaEvent = isUnselecting
            ? AnalyticsConstants.GA_EVENTS.UNSELECT_TOUR
            : AnalyticsConstants.GA_EVENTS.SELECT_TOUR;
        dispatch(analyticsUtils.track.GAEventV2(gaEvent, {
            label: AnalyticsConstants.TOUR_LOCATION.MODAL + AnalyticsConstants.TOUR_TYPE[mode],
        }));
        setSelectedTourId(isUnselecting ? null : id);
    };

    const handleViewTour = (tour) => {
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.VIEW_TOUR, {
            label: AnalyticsConstants.TOUR_LOCATION.MODAL,
        }));
        dispatch(ModalActions.updateModal({
            className: 'ViewTourModal',
            header: tour.name,
            isWizard: true,
            hideBackButton: false,
            modalType: ModalConstants.VIEW_TOUR_MODAL,
            onClickBack: () => {
                dispatch(ModalActions.updateModal({
                    className: 'SelectTourModal',
                    header: `${HEADER_TEXT[mode]} tour`,
                    modalType: ModalConstants.SELECT_TOUR_MODAL,
                    onCancel: () => dispatch(ModalActions.hideModal()),
                    onSubmit,
                    isListingPublished,
                    defaultTours: tours,
                    mode,
                    initialSelectedTourId: selectedTourId,
                    shouldLoadTours: false,
                    isWizard: false,
                    hideBackButton: true,
                }));
            },
            src: tour.src,
            title: tour.name,
        }));
    };

    if (isLoading) {
        return <SpinnerContainer />;
    }

    if (loadingErrorMessage) {
        return <Text fontColor="caution">{loadingErrorMessage}</Text>;
    }

    if (!isLoading && tours.length === 0) {
        return <Text>There are no 3d tours associated with this listing.</Text>;
    }

    const tourChunks = [];
    for (let i = 0; i < tours.length; i += ROW_SIZE) {
        tourChunks.push(tours.slice(i, i + ROW_SIZE));
    }

    return (
        <div className="SelectTourModal">
            <Text paddingBottom="xs" fontType="body" as="div">
                {HELPER_TEXT[mode]}
            </Text>
            {tourChunks.map((tourChunk, chunkIndex) => (
                <Row paddingY="xs" key={`tour-row-${chunkIndex}`}>
                    {tourChunk.map((tour, tourIndex) => (
                        <StyledEvenCol
                            key={tour.id}
                            even={tourIndex % 2 === 0}
                            md={6}
                        >
                            <TourCardSelectable
                                tour={tour}
                                isSelected={tour.id === selectedTourId}
                                onView={() => handleViewTour(tour)}
                                onSelect={() => handleSelectTourId(tour.id)}
                            />
                        </StyledEvenCol>
                    ))}
                </Row>
            ))}
            <Text fontColor="caution">{errorMessage}</Text>
            <StyledControlsDiv>
                <StyledLink
                    aria-label="Cancel"
                    tabIndex={0}
                    role="button"
                    onClick={onCancel}
                    onKeyUp={onKeyUp}
                    disabled={isSubmitting}
                >
                    Cancel
                </StyledLink>
                <Button
                    onClick={handleSubmit}
                    buttonType="primary"
                    disabled={isSubmitting}
                >
                    Save
                </Button>
            </StyledControlsDiv>
        </div>
    );
};

SelectTourModal.propTypes = propTypes;
SelectTourModal.defaultProps = defaultProps;

export { SelectTourModal };
export default connect()(SelectTourModal);
